import type { WidgetLayout } from 'domain/widget';

import type { Widget } from './core-widget-types';

export function getParentSectionId(widget: Widget): string | undefined {
  if (widget.type !== 'section' && widget.type !== 'dynamicSection') {
    return undefined;
  }

  const options = widget.options;
  return options && 'parentSectionId' in options && typeof options.parentSectionId === 'string'
    ? options.parentSectionId
    : undefined;
}

export function getSectionHierarchyErrors(widgets: Widget[]): string[] {
  const errors: string[] = [];
  const widgetsById = new Map(widgets.map((widget) => [widget.id, widget]));

  for (const widget of widgets) {
    const options = widget.options;

    if (!options || !('parentSectionId' in options) || options.parentSectionId === undefined) {
      continue;
    }

    if (widget.type !== 'section' && widget.type !== 'dynamicSection') {
      errors.push(`Widget "${widget.id}" cannot have a parent section.`);
      continue;
    }

    const parentId = options.parentSectionId;

    if (typeof parentId !== 'string' || !parentId.length) {
      errors.push(`Section "${widget.id}" must have a non-empty string parentSectionId.`);
      continue;
    }

    if (parentId === widget.id) {
      errors.push(`Section "${widget.id}" cannot be its own parent.`);
    }

    const parent = widgetsById.get(parentId);

    if (!parent) {
      errors.push(`Parent section "${parentId}" of "${widget.id}" must exist in the same grid.`);
      continue;
    }

    if (parent.type !== 'section') {
      errors.push(`Parent "${parentId}" of "${widget.id}" must be a regular section.`);
    }

    if (getParentSectionId(parent) !== undefined) {
      errors.push(
        `Parent section "${parentId}" of "${widget.id}" must be a root section (maximum depth is 2).`,
      );
    }
  }

  return errors;
}

export function getSectionDescendantIds(
  widgets: Widget[],
  layouts: WidgetLayout[],
  sectionId: string,
): Set<string> {
  const widgetsById = new Map(widgets.map((widget) => [widget.id, widget]));
  const descendants = new Set<string>();

  if (widgetsById.get(sectionId)?.type !== 'section') {
    return descendants;
  }

  const sectionIds = new Set([sectionId]);

  for (const widget of widgets) {
    if (getParentSectionId(widget) === sectionId && widget.id !== sectionId) {
      descendants.add(widget.id);

      if (widget.type === 'section') {
        sectionIds.add(widget.id);
      }
    }
  }

  let currentSection: string | undefined;

  for (const { id } of layouts.toSorted((a, b) => a.y - b.y || a.x - b.x)) {
    const widget = widgetsById.get(id);

    if (widget?.type === 'section') {
      currentSection = id;
    } else if (widget?.type === 'dynamicSection') {
      currentSection = undefined;
    } else if (widget && currentSection && sectionIds.has(currentSection)) {
      descendants.add(id);
    }
  }

  return descendants;
}

export function getSectionHierarchyLayoutErrors(
  widgets: Widget[],
  layouts: WidgetLayout[],
): string[] {
  const widgetsById = new Map(widgets.map((widget) => [widget.id, widget]));
  const errors: string[] = [];
  let rootId: string | undefined;

  for (const { id } of layouts.toSorted((a, b) => a.y - b.y || a.x - b.x)) {
    const widget = widgetsById.get(id);

    if (!widget || (widget.type !== 'section' && widget.type !== 'dynamicSection')) {
      continue;
    }

    const parentId = getParentSectionId(widget);

    if (parentId === undefined) {
      rootId = widget.type === 'section' ? id : undefined;
    } else if (parentId !== rootId) {
      errors.push(`Section "${id}" must stay inside parent section "${parentId}".`);
    }
  }

  return errors;
}
